/** @module */

import { REHYDRATE } from 'redux-persist/constants'
import { path } from 'ramda'
import jwtDecode from 'jwt-decode'

import { createReducer } from '../lib/reducer'
import ActionTypes from '../actions/types'
import { getToken, setToken, removeToken } from '../lib/storage'

/**
 * Safely decode a JWT, return null if invalid
 * @param {string} token
 * @returns {Object|null}
 */
const safeDecodeJwt = (token) => {
  try {
    return jwtDecode(token)
  } catch (error) {
    console.error('Invalid JWT:', error)
    return null
  }
}

/**
 * Common function to update auth state with token and decoded JWT.
 * Handles invalid JWT and fallback to null
 * @param {Object} state
 * @param {string} token
 * @param {boolean} rememberMe
 * @returns {Object}
 */
const setAuthState = (state, token, rememberMe = false, restoredFromLocal = false) => {
  const decodedJwt = safeDecodeJwt(token)

  if (!decodedJwt) {
    removeToken()
    return {
      ...state,
      token: null,
      decodedJwt: null
    }
  }

  const isLoggedInToken = Boolean(decodedJwt.accountId)
  // save logged out token, or logged in and has chosen "remember me" option
  if (!isLoggedInToken || rememberMe || state.rememberMe) {
    setToken(token)
  }
  // when logged out, rememberMe is set to false; when logged in, respect user's selection on login modal
  const rememberMeState = isLoggedInToken ? rememberMe || restoredFromLocal || state.rememberMe : false
  return {
    ...state,
    token,
    decodedJwt,
    rememberMe: rememberMeState
  }
}

const authReducer = createReducer({}, {
  /**
   * Handles user login, stores and decodes JWT
   * @public
   * @name UserLoginHandler
   * @param {Object} state              - The previous state of the store
   * @param {string} [state.token]      - The still-encoded JWT
   * @param {Object} [state.decodedJwt] - The decoded data from the JWT
   * @param {Object} action             - The action
   * @param {string} action.type        - *MUST* be set to `ActionTypes.USER_LOGIN`
   * @param {Account} action.login      - This is the result of the `login` GraphQL query.
   * @param {boolean} action.rememberMe - Persist the user session between browser sessions
   */
  [ActionTypes.USER_LOGIN]: (state, { login, rememberMe }) => {
    const token = path(['session', 'token'], login)
    const authState = setAuthState(state, token, rememberMe)
    return {
      ...authState,
      logInTimestamp: Date.now()
    }
  },

  /**
   * Clears auth state on logout.
   */
  [ActionTypes.USER_LOGOUT]: (state) => {
    removeToken()
    return {
      ...state,
      token: null,
      decodedJwt: null,
      logOutTimestamp: Date.now()
    }
  },

  /**
   * Handles token update via external source (e.g profile switch)
   */
  [ActionTypes.UPDATE_AUTH_TOKEN]: (state, { token }) => {
    return setAuthState(state, token)
  },

  [ActionTypes.UPDATE_AUTH_TOKEN_AND_THEME]: (state, { token }) => {
    return setAuthState(state, token)
  },

  [REHYDRATE]: (state) => {
    const token = getToken()
    return setAuthState(state, token, undefined, true)
  }
})

export default authReducer
